import { get, post, put } from './api'

import { daysOfWeek } from '../constant/days-of-week.constant'
import levelComplexityConstant from '../constant/level.complexity.constant'

const CorrespondentService = {}

CorrespondentService.findHistoryByCorrespondentId = async (id) => get({ type: 'user', service: `correspondents/${id}/history` })

CorrespondentService.filter = async (data) => await post({ type: 'user', service: 'correspondents/filter', data })

CorrespondentService.findOptions = async ({ search, limit } = {}) =>
    await get({ type: 'user', service: 'correspondents/options', queryString: { search, limit } })

CorrespondentService.findById = async (id) => await get({ type: 'user', service: `correspondents/${id}` })

CorrespondentService.findAvailabilityByUserId = async (userId) => await get({ type: 'user', service: `correspondents/availability/${userId}` })

CorrespondentService.findFeedbacksFaciliter = async (userId) => await get({ type: 'user', service: `correspondents/faciliters/${userId}/feedbacks` })

CorrespondentService.filterFeedbacksFaciliter = async (filter) =>
    await post({ type: 'user', service: 'correspondents/faciliters/feedbacks-filter', data: filter })

CorrespondentService.getAreas = async () => await get({ type: 'user', service: 'correspondents/faciliters/areas' })

CorrespondentService.getServices = async () => await get({ type: 'user', service: 'correspondents/service-types' })

CorrespondentService.getAvailabilityCapacityParams = async () =>
    await get({ type: 'user', service: 'correspondents/availability-capacity-params' })

CorrespondentService.getPetitionModalities = async () => await post({ type: 'user', service: 'requests/modalities/services', data: [1] })

CorrespondentService.getUUID = async () => await get({ type: 'user', service: 'correspondents/external-uuid' })

CorrespondentService.getInvoices = async (userId) => await get({ type: 'user', service: `correspondents/${userId}/invoice` })

CorrespondentService.getInvoicesByPeriod = async (userId, period) =>
    await get({ type: 'user', service: `correspondents/${userId}/invoice?period=${period}` })

CorrespondentService.getInvoiceRequests = async (userId, invoiceId) =>
    await get({ type: 'user', service: `correspondents/${userId}/invoice/${invoiceId}/requests` })

CorrespondentService.getInformationLink = async (faciliterId) =>
    await get({ type: 'user', service: `correspondents/${faciliterId}/information-link` })

CorrespondentService.saveAction = async (correspondent) =>
    correspondent.id ? CorrespondentService.edit(correspondent.id, { ...correspondent }) : CorrespondentService.save({ ...correspondent })

const normalizeHearingDistrictsV2 = (hearingDistrictsV2) => {
    // Se já for string, assume que está no formato correto
    if (typeof hearingDistrictsV2 === 'string') {
        return hearingDistrictsV2;
    }

    // Se for array, converte para array de IDs
    if (Array.isArray(hearingDistrictsV2)) {
        const ids = hearingDistrictsV2.map(item =>
            typeof item === 'object' ? item.cityId : item
        );
        return JSON.stringify(ids);
    }

    return hearingDistrictsV2;
}

CorrespondentService.save = async (data) => {
    if (data.hearingDistrictsV2) {
        data.hearingDistrictsV2 = normalizeHearingDistrictsV2(data.hearingDistrictsV2);
    }
    return await post({ type: 'user', service: 'correspondents', data: data })
}

CorrespondentService.externalSave = async (data) => {
    if (data.hearingDistrictsV2) {
        data.hearingDistrictsV2 = normalizeHearingDistrictsV2(data.hearingDistrictsV2);
    }
    return await post({ type: 'user', service: 'correspondents/' + data.uuid, data: data })
}

CorrespondentService.editAvailability = async (id, faciliterInfo, availability) =>
    await put({ type: 'user', service: `correspondents/${id}`, data: { faciliterInfo: { ...faciliterInfo, availability } } });

CorrespondentService.edit = async (id, data) => {

    Object.keys(data).forEach((key) => !getFieldsToEdit().includes(key) && delete data[key]);

    // Normaliza hearingDistrictsV2 para o formato correto (array de IDs como JSON string)
    if (data.hearingDistrictsV2) {
        data.hearingDistrictsV2 = normalizeHearingDistrictsV2(data.hearingDistrictsV2);
    }

    return await put({ type: 'user', service: `correspondents/${id}`, data });
}

CorrespondentService.action = async (id, reason) => put({ type: 'user', service: `correspondents/${id}/action`, data: reason })

CorrespondentService.concludedRating = async (rating, members, isAnalyseRating = false) =>
    await Promise.all(
        members.map(
            async (member) =>
                await put({
                    type: 'user',
                    service: `requests/${rating.requestId}/members/rating/concluded`,
                    data: { ...rating, userId: member.userId, isAnalyseRating },
                }),
        ),
    )

CorrespondentService.mapCorrespondent = (correspondent) => {
    let faciliterInfo = !correspondent.isFaciliter ? newFaciliterInfo() : correspondent.faciliterInfo
    let bankData = !correspondent.bankData ? newBankData() : correspondent.bankData

    return { ...correspondent, faciliterInfo, bankData }
}

CorrespondentService.newCorrespondent = () => ({
    name: '',
    email: '',
    rg: '',
    cpf: '',
    oab: '',
    oabUf: '',
    cellphone: '',
    isFaciliter: false,
    inactive: false,
    faciliterInfo: newFaciliterInfo(),
    bankData: newBankData(),
    address: newAddress(),
})

CorrespondentService.mapToExcel = (correspondents) =>
    correspondents.map((item) => ({
        ID: item.id,
        Nome: item.name,
        Email: item.email,
        RG: item.rg,
        CPF: item.cpf,
        Celular: item.cellphone,
        OAB: item.oab,
        'OAB UF': item.oabUf ? new Date(item.created).toLocaleDateString() : null,
        Disponibilidade: CorrespondentService.getAvailability(item.faciliterInfo),
        Especialidade: (item.specialties || '').toString(),
        'Nível de Complexidade':
            item.additionalInformation && item.additionalInformation.levelComplexity
                ? levelComplexityConstant[item.additionalInformation.levelComplexity].label
                : '',
        Score: item.score && parseFloat(item.score).toFixed(2),
        'Petições Na Semana': item.totalWeek,
        'Petições Em Andamento': item.inProgress,
        'Petições Não Iniciadas': item.notStarted,
        conta: (item.bankData || {}).account,
        agencia: (item.bankData || {}).agency,
        banco: (item.bankData || {}).bank,
        PIX: (item.bankData || {}).pix,
        "Favorecido": item?.bankData?.favored,
        "CPF/CNPJ do Titular da Conta": item?.bankData?.accountOwnerIdentification

    }))

CorrespondentService.getAvailability = (faciliterInfo) => {
    const availability = faciliterInfo?.availability

    if (availability?.days)
        return formatDaysAvailability(availability)

    return `${getAvailabilityDays(faciliterInfo, 'startDay')} - ${getAvailabilityDays(faciliterInfo, 'endDay')} / ${getAvailabilityHours(
        faciliterInfo,
        'startHour',
    )} - ${getAvailabilityHours(faciliterInfo, 'endHour')}`
}

CorrespondentService.recoverSpecialties = (specialties, correspondent) => {
    if (!correspondent.isFaciliter || !(correspondent.specialties || []).length > 0) return { faciliterSpecialties: [], faciliterSubspecialties: [] }

    const faciliterSpecialties = specialties
        .filter((s) => !s.parentId && (correspondent.specialties || []).find((cs) => cs == s.name))
        .map((s) => s.name)

    const faciliterSubspecialties = specialties
        .filter((s) => s.parentId && (correspondent.specialties || []).find((cs) => cs == s.name))
        .map((s) => s.name)

    return { faciliterSpecialties, faciliterSubspecialties }
}

let newBankData = () => ({ bank: '', agency: '', account: '', pix: '' })

const buildDefaultAvailabilityDays = () =>
    daysOfWeek.reduce(
        (days, day) => ({
            ...days,
            [day.name]: { active: !day.isWeekend, startHour: '08:00', endHour: '18:00' },
        }),
        {},
    )

let newFaciliterInfo = () => ({
    availability: { days: buildDefaultAvailabilityDays(), holiday: false, dailyLimit: null },
    specialties: [],
})

const formatDaysAvailability = (availability) => {
    const activeDays = daysOfWeek.filter((day) => availability.days?.[day.name]?.active)

    if (!activeDays.length)
        return '—'

    return activeDays
        .map((day) => {
            const slot = availability.days[day.name]
            return `${day.label} ${slot.startHour || ''}-${slot.endHour || ''}`.trim()
        })
        .join('; ')
}

let newAddress = () => ({ street: '', district: '', zipCode: '', number: '' })

let getAvailabilityDays = (faciliterInfo, day) => {
    if (faciliterInfo && faciliterInfo.availability) {
        let availability = faciliterInfo.availability
        return daysOfWeek[availability[day]]?.label ?? null
    }

    return null
}

let getAvailabilityHours = (faciliterInfo, hour) => {
    if (faciliterInfo && faciliterInfo.availability) return faciliterInfo.availability[hour]

    return null
}

const getFieldsToEdit = () => [
    "name",
    "email",
    "oab",
    "oabUf",
    "rg",
    "cpf",
    "cellphone",
    "bankData",
    "faciliterInfo",
    "additionalInformation",
    "address",
    "hearingDistricts",
    "hearingDistrictsV2",
    "doesOnlyHearing"
];

export default CorrespondentService
